import ControllerBase from '../../../../global/js/ControllerBase';
import { MoleculeBreadcrumbCrumbs, MoleculeBreadcrumbOptions } from './m-breadcrumb-dynamic.data';
import { fromEvent, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

export default class ControllerBreadcrumbDynamic extends ControllerBase {
	/** The component name */
	private readonly componentName = 'm-breadcrumb-dynamic';

	/** A list of dom element references available to this component */
	private readonly componentRefs = {
		nav: 'nav',
	};

	/** The parsed breadcrumb data */
	private breadcrumbs: MoleculeBreadcrumbCrumbs[];

	/** The parsed breadcrumb options */
	private options: MoleculeBreadcrumbOptions;

	/** The current crumb depth, starting from 0 */
	private currentDepth: number;

	/** A list of current children for the clicked crumb */
	private currentChildren: MoleculeBreadcrumbCrumbs[] = [];

	/** A list of the child crumbs of to the latest crumb */
	private allChildren: MoleculeBreadcrumbCrumbs[][] = [];

	/** A list of the top-level crumbs */
	private crumbsArray: MoleculeBreadcrumbCrumbs[] = [];

	/** A list of subscriptions for click handling */
	private clickHandlers: Subscription[] = [];

	/** handle subscriptions/memory */
	// private _unsubscribeAll: Subject<any>;

	constructor(element: HTMLElement) {
		super(element);

		// initialise the crumb depth counter
		this.currentDepth = -1;

		// de-serialise/parse the crumb data and init
		if (this.$element.dataset?.breadcrumbs) {
			// parse the crumb options
			if (this.$element.dataset?.options) {
				this.options = JSON.parse(this.$element.dataset.options);
			}

			// parse breadcrumbs
			this.breadcrumbs = JSON.parse(this.$element.dataset.breadcrumbs);

			const list = this.getHtmlListElement();
			// render initial crumbs
			if (list && this.breadcrumbs) {
				// reset entire crumb html to empty
				list.innerHTML = '';
				// render home
				this.renderHomeCrumb();

				this.initBreadcrumbs();

				// watch for crumb clicks on crumbs
				this.observeCrumbClicks();
			}
		}

		// this._unsubscribeAll = new Subject();

		// window.addEventListener('unload', (e) => {
		// 	this._unsubscribeAll.next();
		// 	this._unsubscribeAll.complete();
		// });
	}

	/**
	 * Initialise the breadcrumbs by rendering the first set of crumbs to the page
	 * @returns void
	 */
	private initBreadcrumbs(): void {
		const list = this.getHtmlListElement();

		// the top level items which will appear in the first menu
		const topLevelParents = this.getTopLevelCrumbs();
		// true if any of the items have children
		const itemsHaveChildren = this.crumbsHaveChildren(topLevelParents);

		// add the parent node for the first crumb (children appended later)
		this.addParentNode(list, topLevelParents[0], {
			hasChildren: itemsHaveChildren,
			crumbTitle: this.options.newCrumbTitle,
		});

		// add each top-level parent to the sub menu
		this.createNewBreadcrumbMenu(topLevelParents);

		// set the current breadcrumb children to be the first level of items
		this.currentChildren = this.getTopLevelCrumbs();
		// remember this list for later
		this.allChildren.push(this.getTopLevelCrumbs());

		this.buildActiveCrumbsRecursive(this.getTopLevelCrumbs(false));
	}

	private buildActiveCrumbsRecursive(crumbs: MoleculeBreadcrumbCrumbs[]): void {
		if (crumbs) {
			// check if there are any active crumbs
			const activeCrumb = this.getActiveCrumb(crumbs);

			if (activeCrumb) {
				// init crumbs with active path
				const linkNodes = this.getHtmlListElement().querySelectorAll(':scope a') as NodeListOf<HTMLAnchorElement>;
				if (linkNodes) {
					const nodes: HTMLAnchorElement[] = Array.from(linkNodes).filter((n: HTMLAnchorElement) => 'active' in n.dataset) || [];

					if (nodes && nodes.length > 0) {
						this.buildNextCrumbNode(nodes[nodes.length - 1]);
					}
				}
				if (activeCrumb.children) {
					const childActiveCrumbs = this.getActiveCrumb(activeCrumb.children);
					if (childActiveCrumbs && childActiveCrumbs.children && childActiveCrumbs.children.length > 0) {
						// @todo - store childActiveCrumbs for later?
						this.buildActiveCrumbsRecursive(childActiveCrumbs.children);
					}
				} else {
					// if the final crumb has no children, but is active, put the title on the final crumb node

					const list = this.getHtmlListElement();
					const lastNode = list && list.children ? list.children.length - 1 : -1;
					const nodeLi = lastNode > -1 ? list.children[lastNode] : null;
					const nodeAnchor = nodeLi ? (nodeLi?.children[0] as HTMLAnchorElement) : null;
					nodeAnchor ? (nodeAnchor.innerText = activeCrumb.title) : undefined;
				}
			} else {
				// @todo - put previous crumb on the last node label?
				// console.log(crumbs);
				// const activeCrumb: MoleculeBreadcrumbCrumbs = crumbs[0];
				// // if the final crumb has no children, but is active, put the title on the final crumb node
				// if (activeCrumb) {
				// 	const list = this.getHtmlListElement();
				// 	const lastNode = (list && list.children) ? list.children.length - 1 : -1;
				// 	const nodeLi = lastNode > -1 ? list.children[lastNode] : null;
				// 	const nodeAnchor = nodeLi ? nodeLi?.children[0] as HTMLAnchorElement : null;
				// 	nodeAnchor ? nodeAnchor.innerText = activeCrumb.title : undefined;
				// }
			}
		}
	}

	private renderHomeCrumb(): void {
		const list = this.getHtmlListElement();
		const home = this.getHomeCrumb();
		if (home && home.length > 0) {
			this.addParentNode(list, home[0], {
				hasChildren: false,
				crumbTitle: this.options.homeCrumbTitle,
			});
			// add home to the list
			this.allChildren.push(home);
		}
	}

	/**
	 * Get the top level breadcrumbs
	 * @param  {boolean=false} includeHome If true, the home item will be returned also
	 * @returns MoleculeBreadcrumbCrumbs A list of top-level crumbs
	 */
	private getTopLevelCrumbs(includeHome: boolean = false): MoleculeBreadcrumbCrumbs[] {
		if (this.breadcrumbs) {
			return Object.values(this.breadcrumbs).filter((c: MoleculeBreadcrumbCrumbs) => {
				if (includeHome) {
					return c;
				}
				return !c.isHome;
			});
		}
		return [];
	}

	/**
	 * Get the home breadcrumb
	 * @returns MoleculeBreadcrumbCrumbs[]
	 */
	private getHomeCrumb(): MoleculeBreadcrumbCrumbs[] {
		if (this.breadcrumbs) {
			return Object.values(this.breadcrumbs).filter((c: MoleculeBreadcrumbCrumbs) => c.isHome) || [];
		}
		return [];
	}

	/**
	 * Returns true if any of the crumbs given have children crumbs
	 * @param  {MoleculeBreadcrumbCrumbs[]} crumbs
	 * @returns boolean
	 */
	private crumbsHaveChildren(crumbs: MoleculeBreadcrumbCrumbs[]): boolean {
		return crumbs.filter((c) => (c.children && c.children.length ? true : undefined)).filter((c) => c).length > 0;
	}

	/**
	 * Append a new crumb node to the master <ul> and build the sublist dropdown based on the children items
	 * @param  {HTMLUListElement} parentList
	 * @param  {MoleculeBreadcrumbCrumbs} crumb
	 * @param  {boolean} hasChildren?
	 * @returns void
	 */
	private addParentNode(
		parentList: HTMLUListElement,
		crumb: MoleculeBreadcrumbCrumbs,
		options: {
			hasChildren: boolean;
			crumbTitle?: string;
		},
	): void {
		if (crumb) {
			// store a copy of the crumb
			this.crumbsArray.push(crumb);

			const li = this.createNewCrumbListItem(true, options?.hasChildren);
			const liDepth = parentList.children.length;
			li.setAttribute('data-node-depth', liDepth.toString());
			parentList.appendChild(li);

			const crumbLink = this.createNewCrumbLink(crumb, {
				title: options.crumbTitle || crumb.title,
			});
			if (!crumb.isHome) {
				// build a new uikit dropdown holder
				const dropdown = document.createElement<'div'>('div');
				dropdown.classList.add('uk-navbar-dropdown');
				dropdown.setAttribute('data-uk-dropdown', '');
				if (options?.hasChildren) {
					const sublist = document.createElement<'ul'>('ul');
					sublist.classList.add('uk-nav', 'uk-navbar-dropdown-nav');
					dropdown.appendChild(sublist);
				}
				// append link
				li.appendChild(crumbLink);
				// add dropdown with sublist
				li.appendChild(dropdown);
			} else {
				// append link
				li.appendChild(crumbLink);
			}

			// increment crumb depth
			this.currentDepth++;
		}
	}

	/**
	 * Add a menu crumb item to the given <ul> container
	 * @param  {HTMLUListElement} node
	 * @param  {MoleculeBreadcrumbCrumbs} crumb
	 * @param  {number} index
	 * @returns void
	 */
	private appendNodes(node: HTMLUListElement, crumb: MoleculeBreadcrumbCrumbs, index: number): void {
		// create crumb li container
		const crumbElem = this.createNewCrumbListItem(false);
		// create crumb anchor click
		const crumbLink = this.createNewCrumbLink(crumb);

		if (crumb.children && crumb.children.length > 0) {
			crumbElem.setAttribute('data-children', 'true');
			crumbElem.setAttribute('data-index', index.toString());
		}

		crumbElem.appendChild(crumbLink);
		node.appendChild(crumbElem);
	}

	/**
	 * Creates a new crumb <li> container
	 * @param  {boolean=false} hasChildren
	 * @returns HTMLLIElement
	 */
	private createNewCrumbListItem(isTopLevel: boolean, hasChildren: boolean = false): HTMLLIElement {
		const crumbElem = document.createElement<'li'>('li');
		crumbElem.classList.add(this.componentName + '__breadcrumb');

		if (hasChildren) {
			crumbElem.classList.add(this.componentName + '__breadcrumb--dropdown');
		}
		if (!isTopLevel) {
			crumbElem.classList.add(this.componentName + '__breadcrumb--menuitem');
		}
		return crumbElem;
	}

	/**
	 * Creates a new crumb anchor with text
	 * @param  {string} innerText
	 * @param  {{title:string}} options? Custom options for the link
	 * @returns HTMLAnchorElement
	 */
	private createNewCrumbLink(crumb: MoleculeBreadcrumbCrumbs, options?: { title: string }): HTMLAnchorElement {
		const crumbLink = document.createElement<'a'>('a');
		if (options?.title) {
			crumbLink.innerText = options.title;
		} else if (crumb.title) {
			crumbLink.innerText = crumb.title;
		}

		if (crumb.href && this.crumbHasChildren(crumb) === false) {
			// add a href
			crumbLink.href = crumb.href;
		}
		if (crumb.isActive) {
			crumbLink.setAttribute('data-active', 'true');
		}
		return crumbLink;
	}

	/**
	 * Remove crumbs from the DOM after and including a certain index
	 * @param  {number} nodeIndex
	 * @returns void
	 */
	private removeCrumbs(nodeIndex: number): void {
		// make sure we don't remove the home crumb
		if (nodeIndex > 0 && this.crumbsArray.length > nodeIndex) {
			this.crumbsArray.splice(nodeIndex);

			// remove from the DOM
			const ul = this.getHtmlListElement();
			// while (ul.childNodes.length > ul.childNodes.length - nodeIndex) {
			while (ul.childNodes.length > nodeIndex) {
				ul.removeChild(ul.childNodes[ul.childNodes.length - 1]);
			}
			// reset node depth
			this.currentDepth = nodeIndex - 1;
		}
	}

	/**
	 * Build next crumb node with dropdown list of child crumbs, if any
	 * @param  {HTMLAnchorElement} anchorElem
	 * @returns void
	 */
	private buildNextCrumbNode(anchorElem: HTMLAnchorElement): void {
		const parentLi = anchorElem.closest<'li'>('li');
		let crumbNodeIndex = -1;

		// the index of this list item
		const childIndex = Number(parentLi?.getAttribute('data-index')) || 0;

		// the grandparent li contains the node index
		const grandParentLi = parentLi ? parentLi.parentElement?.closest<'li'>('li') : undefined;
		if (grandParentLi) {
			const index = grandParentLi.getAttribute('data-node-depth');
			// the crumb depth of the top level node, from zero
			crumbNodeIndex = Number(index);

			// update the main breadcrumb label with the label from the one the user has clicked on
			const crumbAnchor = grandParentLi.children[0] as HTMLAnchorElement;
			crumbAnchor && this.updateCrumbLabel(crumbAnchor, anchorElem.innerText);
		}

		// check to see if one of the existing or new crumbs were clicked
		if (crumbNodeIndex < this.currentDepth) {
			// update the dom...
			// remove crumb nodes after the index of the node clicked

			// remove the crumbs after crumbNodeIndex
			this.removeCrumbs(crumbNodeIndex + 1);
			// update list of children in the crumb list
			this.allChildren.splice(crumbNodeIndex + 1);
			// remember the new children
			this.currentChildren = this.allChildren[crumbNodeIndex] || [];
		}

		const targetChildren = this.currentChildren.length >= childIndex ? this.currentChildren[childIndex]?.children || [] : [];

		// store the next set of children for building DOM nodes
		this.currentChildren = targetChildren;
		// add the children for later
		this.allChildren.push(targetChildren);
		// check if new crumb has children
		const targetHasChildren = this.currentChildren.length > 0;

		// add the parent node
		this.addParentNode(this.getHtmlListElement(), this.currentChildren[0], {
			hasChildren: targetHasChildren,
			crumbTitle: this.options.newCrumbTitle,
		});

		// add these children to the new node
		this.createNewBreadcrumbMenu(this.currentChildren);
	}

	/**
	 * Updates anchor text with a given text string
	 * @param  {HTMLAnchorElement} anchor The anchor to be updated
	 * @param  {string} label
	 * @returns void
	 */
	private updateCrumbLabel(anchor: HTMLAnchorElement, label: string): void {
		if (anchor) {
			anchor.innerText = label;
		}
	}

	/**
	 * Adds elements to the last dropdown menu
	 * @param  {MoleculeBreadcrumbCrumbs[]} elements
	 * @returns void
	 */
	private createNewBreadcrumbMenu(elements: MoleculeBreadcrumbCrumbs[]): void {
		elements &&
			elements.forEach((element, index) => {
				const sublist = this.getHtmlListElement().querySelectorAll<'ul'>('ul');
				sublist && sublist.length && this.appendNodes(sublist[sublist.length - 1], element, index);
			});
	}

	/**
	 * Observe clicking on crumbs
	 */
	private observeCrumbClicks(): void {
		const ulNav = this.getHtmlListElement();
		const crumbAnchors = ulNav && ulNav.querySelectorAll<'a'>('a');

		// unsubscribe from previous click events
		// @todo could use takeUntil in the pipe?
		this.clickHandlers && this.clickHandlers.forEach((o) => o && o.unsubscribe());

		crumbAnchors &&
			crumbAnchors.forEach((anchor) => {
				if (anchor) {
					this.clickHandlers.push(
						fromEvent(anchor, 'click')
							.pipe(
								// takeUntil(this._unsubscribeAll),
								map((e: Event) => e && e.target),
							)
							.subscribe((et: EventTarget & HTMLAnchorElement) => {
								this.buildNextCrumbNode(et);
								// re-observe crumb clicks since the DOM has changed
								this.observeCrumbClicks();
							}),
					);
				}
			});
	}

	/**
	 * Gets a reference to the top-level <ul> HTML Element
	 * @returns HTMLUListElement
	 */
	private getHtmlListElement(): HTMLUListElement {
		return this.$references && (this.$references[this.componentRefs.nav] as HTMLUListElement);
	}

	/**
	 * Returns true if this crumb has any child crumbs
	 * @param  {MoleculeBreadcrumbCrumbs} crumb The crumb to test
	 * @returns boolean
	 */
	private crumbHasChildren(crumb: MoleculeBreadcrumbCrumbs): boolean {
		return !!(crumb.children && crumb.children.length > 0);
	}

	/**
	 * Returns the first crumb in the list of crumbs given, which has isActive=true
	 * @returns boolean
	 */
	private getActiveCrumb(crumbs?: MoleculeBreadcrumbCrumbs[]): MoleculeBreadcrumbCrumbs | null {
		const _crumb = crumbs ? crumbs : this.breadcrumbs;
		if (_crumb) {
			const activeCrumb = _crumb.filter((b) => b.isActive === true);
			if (activeCrumb.length > 0) {
				return activeCrumb[0];
			}
		}
		return null;
	}
}
